import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import StudentLayout from "../../Student/Layout/index.jsx";
import {
  useGetAllAssignmentQuestionQuery,
  useGetAssignmentQuery,
  useGetAssignmentSubmitQuery,
  useGetCampusQuery,
  useGetMessageByScoreQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";

const AssignmentSubmitSummary = () => {
  const { assignmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const { data: student } = useGetStudentByEmailIdQuery({ variables: { email }, skip: !email });
  const { data: campus } = useGetCampusQuery({
    variables: { id: student?.getStudentByEmailId?.campusId },
    skip: !student?.getStudentByEmailId,
  });
  const { data: assignmentData } = useGetAssignmentQuery({ variables: { id: assignmentId }, skip: !assignmentId });
  const { data: assignmentQuestions } = useGetAllAssignmentQuestionQuery({ variables: { assignmentId }, skip: !assignmentId });
  const { data: submissionQueryData } = useGetAssignmentSubmitQuery({
    variables: {
      assignmentId,
      studentId: student?.getStudentByEmailId?.id,
      campus_name: campus?.campus?.name,
    },
    skip: !student?.getStudentByEmailId || !campus?.campus?.name,
    fetchPolicy: "network-only",
  });

  const submission = useMemo(() => {
    return (
      submissionQueryData?.getAssignmentSubmit ||
      location?.state?.submission || {
        id: "fallback",
        assignment_id: assignmentId,
        questions: [
          { question_id: "q1", score: 50, language: "JAVA", __typename: "QuestionSubmission" },
          { question_id: "q2", score: 66, language: "JAVA", __typename: "QuestionSubmission" },
        ],
        totalScore: 116,
        tabSwitches: 0,
        cheat_reasons: "",
        endTime: new Date().toISOString(),
        completed: true,
        timeRemaining: 0,
        __typename: "AssignmentSubmit",
      }
    );
  }, [submissionQueryData, location?.state?.submission, assignmentId]);

  const questionCount = submission?.questions?.length || 0;
  const totalScore = submission?.totalScore ?? 0;

  const totalQuestions = assignmentQuestions?.getAllAssignmentQuestion?.length || 0;

  const maxScore = useMemo(() => {
    const list = assignmentQuestions?.getAllAssignmentQuestion || [];
    return list.reduce((acc, q) => {
      const n = Number(q?.marks);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [assignmentQuestions?.getAllAssignmentQuestion]);

  const questionStats = useMemo(() => {
    const submitted = submission?.questions || [];
    const questionList = assignmentQuestions?.getAllAssignmentQuestion || [];

    // Any submitted entry counts as attended.
    const attendedIds = new Set(submitted.map((q) => q.question_id));

    const marksByQuestionId = new Map(
      questionList.map((q) => [q.id, Number(q?.marks || 0)])
    );

    // Completed means student got full marks for that question.
    const completedIds = new Set(
      submitted
        .filter((q) => {
          const fullMarks = marksByQuestionId.get(q.question_id);
          return Number.isFinite(fullMarks) && fullMarks > 0 && Number(q?.score || 0) >= fullMarks;
        })
        .map((q) => q.question_id)
    );

    return {
      attendedCount: attendedIds.size,
      completedCount: completedIds.size,
    };
  }, [assignmentQuestions?.getAllAssignmentQuestion, submission?.questions]);

  const scorePercent = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
  const firstName = (student?.getStudentByEmailId?.name || "User").split(" ")[0] || "User";

  const { data: reportMessageData } = useGetMessageByScoreQuery({
    variables: { scorePercent, campusName: campus?.campus?.name },
    skip: !Number.isFinite(scorePercent),
    fetchPolicy: "network-only",
  });

  const durationHours = assignmentData?.getAssignment?.duration ?? 0;
  const allowedMs = durationHours > 0 ? durationHours * 60 * 60 * 1000 : 0;
  const allowedMin = Math.round(allowedMs / 60000);
  const remainingMs = submission?.timeRemaining ?? 0;
  const takenMs = allowedMs > 0 ? Math.max(0, allowedMs - remainingMs) : 0;
  const takenMin = Math.round(takenMs / 60000);
  const remainingMin = allowedMin - takenMin;
  const takenPct = allowedMin > 0 ? Math.round((takenMin / allowedMin) * 100) : 0;
  const remainingPct = 100 - takenPct;

  const formatMinutesAsDuration = (totalMinutes) => {
    const safeMinutes = Math.max(0, Math.round(Number(totalMinutes) || 0));
    if (safeMinutes === 0) return "0m";

    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };
  const assignmentTitle = assignmentData?.getAssignment?.title || assignmentData?.getAssignment?.name || "Assignment";
  const submittedLabel = submission?.completed ? "Submitted" : "In progress";
  const difficultyCounts = useMemo(() => {
    const list = assignmentQuestions?.getAllAssignmentQuestion || [];
    return list.reduce(
      (acc, q) => {
        const d = String(q?.difficulty || "unknown").toLowerCase();
        if (d === "easy") acc.easy += 1;
        else if (d === "medium") acc.medium += 1;
        else if (d === "hard") acc.hard += 1;
        else acc.unknown += 1;
        return acc;
      },
      { easy: 0, medium: 0, hard: 0, unknown: 0 }
    );
  }, [assignmentQuestions?.getAllAssignmentQuestion]);

  const attemptedDifficultyCounts = useMemo(() => {
    const counts = { easy: 0, medium: 0, hard: 0, unknown: 0 };
    const questions = assignmentQuestions?.getAllAssignmentQuestion || [];
    const submitted = submission?.questions || [];

    const difficultyByQuestionId = new Map(
      questions.map((q) => [q.id, String(q?.difficulty || "unknown").toLowerCase()])
    );

    submitted.forEach((s) => {
      const difficulty = difficultyByQuestionId.get(s.question_id);
      if (difficulty === "easy") counts.easy += 1;
      else if (difficulty === "medium") counts.medium += 1;
      else if (difficulty === "hard") counts.hard += 1;
      else counts.unknown += 1;
    });

    return counts;
  }, [assignmentQuestions?.getAllAssignmentQuestion, submission?.questions]);

  const difficultyCoverage = useMemo(() => {
    const calc = (attempted, total) => (total > 0 ? Math.round((attempted / total) * 100) : 0);
    return {
      easy: calc(attemptedDifficultyCounts.easy, difficultyCounts.easy),
      medium: calc(attemptedDifficultyCounts.medium, difficultyCounts.medium),
      hard: calc(attemptedDifficultyCounts.hard, difficultyCounts.hard),
      unknown: calc(attemptedDifficultyCounts.unknown, difficultyCounts.unknown),
    };
  }, [attemptedDifficultyCounts, difficultyCounts]);

  const difficultyCoverageRows = useMemo(() => {
    const rows = [
      {
        key: "easy",
        label: "Easy",
        total: difficultyCounts.easy,
        attempted: attemptedDifficultyCounts.easy,
        pct: difficultyCoverage.easy,
        tone: "bg-emerald-50 text-emerald-700",
      },
      {
        key: "medium",
        label: "Medium",
        total: difficultyCounts.medium,
        attempted: attemptedDifficultyCounts.medium,
        pct: difficultyCoverage.medium,
        tone: "bg-amber-50 text-amber-700",
      },
      {
        key: "hard",
        label: "Hard",
        total: difficultyCounts.hard,
        attempted: attemptedDifficultyCounts.hard,
        pct: difficultyCoverage.hard,
        tone: "bg-rose-50 text-rose-700",
      },
      {
        key: "unknown",
        label: "Unspecified",
        total: difficultyCounts.unknown,
        attempted: attemptedDifficultyCounts.unknown,
        pct: difficultyCoverage.unknown,
        tone: "bg-slate-100 text-slate-700",
      },
    ];

    // Always show Easy/Medium/Hard even if total is 0.
    return rows.filter((row) => row.key !== "unknown" || row.total > 0);
  }, [attemptedDifficultyCounts, difficultyCounts, difficultyCoverage]);

  const languageUsage = useMemo(() => {
    const submitted = submission?.questions || [];
    const counts = submitted.reduce((acc, q) => {
      const lang = String(q?.language || "Unknown").trim() || "Unknown";
      const key = lang.toUpperCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([language, count]) => ({ language, count }))
      .sort((a, b) => b.count - a.count);
  }, [submission?.questions]);

  const hasCampusName = Boolean(campus?.campus?.name);
  const hasReportMessagePayload = Boolean(reportMessageData?.getReportMessageByScore);
  const useSimpleFallbackMessage = !hasCampusName || !hasReportMessagePayload;

  const coachMessage = useMemo(() => {
    if (useSimpleFallbackMessage) {
      return `Hey ${firstName}`;
    }

    const response = reportMessageData?.getReportMessageByScore;
    const list = Array.isArray(response) ? response : response ? [response] : [];

    if (!list.length) return `Heyy ${firstName}, keep pushing forward!`;

    const randomIndex = Math.floor(Math.random() * list.length);
    const selected = list[randomIndex] || {};
    const message = String(selected?.message || "").trim();

    if (!message) return `Heyy ${firstName}, keep pushing forward!`;
    return `Heyy ${firstName}, ${message}`.trim();
  }, [reportMessageData, firstName, useSimpleFallbackMessage]);

  const signOffMessage = useSimpleFallbackMessage
    ? ""
    : String(reportMessageData?.getReportMessageByScore?.greeting || "").trim() ||
      "Have a great day.";
  const scorecardIntroMessage = useSimpleFallbackMessage
    ? "Here is your updated scorecard."
    : "Here is your assignment scorecard.";
  const [typedMessage, setTypedMessage] = useState("");
  const [typedSignOff, setTypedSignOff] = useState("");
  const [typedScorecardIntro, setTypedScorecardIntro] = useState("");
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [breakdownVisible, setBreakdownVisible] = useState(false);
  const [displayTotals, setDisplayTotals] = useState({
    totalScore: 0,
    maxScore: 0,
    attendedCount: 0,
    completedCount: 0,
  });
  const [animatedTakenPct, setAnimatedTakenPct] = useState(0);
  const [animatedRemainingPct, setAnimatedRemainingPct] = useState(100);
  const [difficultyBarsVisible, setDifficultyBarsVisible] = useState(false);
  const [timeCardVisible, setTimeCardVisible] = useState(false);
  const [difficultyCardVisible, setDifficultyCardVisible] = useState(false);
  const [visibleStatsCount, setVisibleStatsCount] = useState(0);

  const typeLikeHuman = (text, setText) => {
    const chars = Array.from(String(text || ""));
    let i = 0;
    let cancelled = false;
    let timeoutId;

    const rand = (min, max) => Math.floor(min + Math.random() * (max - min + 1));

    const delayForChar = (ch) => {
      if (ch === "\n") return rand(260, 520);
      if (/[.!?]/.test(ch)) return rand(180, 360);
      if (/[,;:]/.test(ch)) return rand(90, 180);
      return rand(14, 42);
    };

    const tick = () => {
      if (cancelled) return;
      if (i >= chars.length) return;

      const remaining = chars.length - i;
      const roll = Math.random();
      const step = Math.min(remaining, roll < 0.10 ? 3 : roll < 0.25 ? 2 : 1);

      const chunk = chars.slice(i, i + step).join("");
      i += step;
      setText((prev) => prev + chunk);

      const lastChar = chunk[chunk.length - 1];
      const baseDelay = delayForChar(lastChar);
      const jitter = rand(0, 22);
      timeoutId = window.setTimeout(tick, baseDelay + jitter);
    };

    // Initial "thinking" pause.
    timeoutId = window.setTimeout(tick, rand(160, 420));

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  };

  useEffect(() => {
    setTypedMessage("");
    setTypedSignOff("");
    setTypedScorecardIntro("");
    setDetailsVisible(false);
    setHeaderVisible(false);
    setStatsVisible(false);
    setBreakdownVisible(false);
    setVisibleStatsCount(0);
    if (!coachMessage) return;

    return typeLikeHuman(coachMessage, setTypedMessage);
  }, [coachMessage]);

  useEffect(() => {
    if (!coachMessage || !signOffMessage || typedMessage !== coachMessage) return;

    return typeLikeHuman(signOffMessage, setTypedSignOff);
  }, [typedMessage, coachMessage, signOffMessage]);

  useEffect(() => {
    const readyForScorecardIntro = signOffMessage
      ? typedSignOff === signOffMessage
      : typedMessage === coachMessage;
    if (!readyForScorecardIntro) return;

    return typeLikeHuman(scorecardIntroMessage, setTypedScorecardIntro);
  }, [typedSignOff, signOffMessage, typedMessage, coachMessage, scorecardIntroMessage]);

  useEffect(() => {
    if (typedScorecardIntro !== scorecardIntroMessage) return;

    const t1 = window.setTimeout(() => setHeaderVisible(true), 180);
    const t2 = window.setTimeout(() => setStatsVisible(true), 420);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [typedScorecardIntro, scorecardIntroMessage]);

  useEffect(() => {
    if (!statsVisible) {
      setVisibleStatsCount(0);
      return;
    }
    const timers = [1, 2, 3, 4, 5].map((step, idx) =>
      window.setTimeout(() => setVisibleStatsCount(step), idx * 320)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [statsVisible]);

  useEffect(() => {
    if (!statsVisible || visibleStatsCount < 5) return;
    const t = window.setTimeout(() => {
      setBreakdownVisible(true);
      setDetailsVisible(true);
    }, 300);
    return () => window.clearTimeout(t);
  }, [statsVisible, visibleStatsCount]);

  useEffect(() => {
    if (!statsVisible) {
      setDisplayTotals({ totalScore: 0, maxScore: 0, attendedCount: 0, completedCount: 0 });
      return;
    }

    const duration = 850;
    const start = performance.now();
    let raf;

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setDisplayTotals({
        totalScore: Math.round(totalScore * eased),
        maxScore: Math.round(maxScore * eased),
        attendedCount: Math.round(questionStats.attendedCount * eased),
        completedCount: Math.round(questionStats.completedCount * eased),
      });

      if (progress < 1) {
        raf = window.requestAnimationFrame(animate);
      }
    };

    raf = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(raf);
  }, [statsVisible, totalScore, maxScore, questionStats.attendedCount, questionStats.completedCount]);

  useEffect(() => {
    if (!breakdownVisible) {
      setAnimatedTakenPct(0);
      setAnimatedRemainingPct(100);
      setDifficultyBarsVisible(false);
      setTimeCardVisible(false);
      setDifficultyCardVisible(false);
      return;
    }

    const t1 = window.setTimeout(() => {
      setTimeCardVisible(true);
    }, 180);

    const t2 = window.setTimeout(() => {
      setAnimatedTakenPct(takenPct);
      setAnimatedRemainingPct(remainingPct);
    }, 950);

    const t3 = window.setTimeout(() => {
      setDifficultyCardVisible(true);
    }, 1300);

    const t4 = window.setTimeout(() => {
      setDifficultyBarsVisible(true);
    }, 1650);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [breakdownVisible, takenPct, remainingPct]);

  return (
    <StudentLayout>
      <div className="min-h-screen px-3 py-3 bg-white sm:px-4 md:px-6 md:py-4 lg:px-8">
        <div className="relative w-full space-y-2 xl:w-[85%]">
          <button
            type="button"
            aria-label="Close"
            onClick={() =>
              navigate("/assignments", {
                state: { refetchAssignments: true, refetchAt: Date.now() },
              })
            }
            className="absolute z-20 inline-flex items-center justify-center transition bg-white border rounded-full shadow-sm right-2 top-2 h-9 w-9 border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
          >
            ✕
          </button>

          <section className="rounded-3xl bg-white p-2.5  md:p-4">
            <div className="flex items-start gap-3 md:gap-4">
             < img
                src="/images/summary/image.png"
                alt="Bot avatar"
                className="object-cover w-10 h-10 rounded-full shrink-0 md:h-11 md:w-11"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <p className="text-sm font-semibold text-slate-800">Astra </p>
                </div>

                <div className="flex flex-col items-start gap-3 mt-1">
                  <div className="inline-block w-fit max-w-[92%] rounded-2xl bg-slate-100 border border-slate-200 px-4 py-2.5 text-sm leading-6 text-slate-800 md:px-5 md:py-3 md:text-[15px]">
                    <p className="break-words whitespace-pre-wrap">
                      {typedMessage}
                      {typedMessage !== coachMessage ? (
                        <span className="ml-0.5 inline-block animate-pulse text-slate-400">▍</span>
                      ) : null}
                    </p>
                  </div>

                  {typedMessage === coachMessage && signOffMessage ? (
                    <div className="inline-block w-fit max-w-[92%] rounded-2xl bg-slate-100 border border-slate-200 px-4 py-2.5 text-sm leading-6 text-slate-800 md:px-5 md:py-3 md:text-[15px]">
                      <p className="break-words whitespace-pre-wrap">
                        {typedSignOff}
                        {typedSignOff !== signOffMessage ? (
                          <span className="ml-0.5 inline-block animate-pulse text-slate-400">▍</span>
                        ) : null}
                      </p>
                    </div>
                  ) : null}

                  {(signOffMessage ? typedSignOff === signOffMessage : typedMessage === coachMessage) ? (
                    <div className="inline-block w-fit max-w-[92%] rounded-2xl bg-blue-50 border border-blue-100 px-4 py-2.5 text-sm leading-6 text-blue-800 md:px-5 md:py-3 md:text-[15px]">
                      <p className="break-words whitespace-pre-wrap">
                        {typedScorecardIntro}
                        {typedScorecardIntro !== scorecardIntroMessage ? (
                          <span className="ml-0.5 inline-block animate-pulse text-blue-300">▍</span>
                        ) : null}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          {typedScorecardIntro === scorecardIntroMessage && !headerVisible ? (
            <header className="relative px-5 py-3 bg-white shadow-sm rounded-2xl md:px-6 md:py-4 animate-pulse">
              <div className="flex flex-col gap-3">
                <div className="flex justify-center">
                  <div className="h-8 rounded-lg w-72 bg-slate-100" />
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div className="w-64 rounded-lg h-7 bg-slate-100" />
                  <div className="h-5 rounded-lg w-52 bg-slate-100" />
                </div>
              </div>
            </header>
          ) : null}

          {headerVisible ? (
            <header className="relative px-5 py-3 bg-white shadow-sm rounded-2xl md:px-6 md:py-4" style={{ animation: "fadeIn 300ms ease-out" }}>
              <div className="flex flex-col gap-3">
                <div className="flex justify-center">
                  <p className="text-[27px] font-semibold leading-tight text-slate-800">
                    Assessment scorecard
                  </p>
                </div>

                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <h1 className="text-[21px] font-medium leading-tight text-slate-900">{assignmentTitle}</h1>
                  <p className="text-sm text-slate-500 sm:text-right">
                    {questionCount} question{questionCount !== 1 ? "s" : ""} · {totalQuestions || questionCount} total · {submittedLabel}
                  </p>
                </div>
              </div>
            </header>
          ) : null}

          {headerVisible && !statsVisible ? (
            <section className="px-4 py-3 bg-white shadow-sm rounded-2xl md:px-6 animate-pulse">
              <div className="grid gap-3 md:grid-cols-5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="min-h-[112px] rounded-xl bg-slate-100" />
                ))}
              </div>
            </section>
          ) : null}

          {statsVisible ? (
            <section className="rounded-2xl bg-white px-4 py-2.5 shadow-sm md:px-6" style={{ animation: "fadeIn 320ms ease-out" }}>
              <div className="grid gap-2 md:grid-cols-5">
                {visibleStatsCount >= 1 ? (
                <div className="flex min-h-[112px] flex-col items-center justify-center text-center" style={{ animation: "scorecardItemEnter 320ms ease-out both" }}>
                  <p className="text-xs font-semibold text-slate-600">Total score</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">{displayTotals.totalScore}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{scorePercent}% of max score</p>
                </div>
                ) : <div className="min-h-[112px] rounded-xl bg-slate-100 animate-pulse" />}
                {visibleStatsCount >= 2 ? (
                <div className="flex min-h-[112px] flex-col items-center justify-center text-center" style={{ animation: "scorecardItemEnter 320ms ease-out both" }}>
                  <p className="text-xs font-semibold text-slate-600">Max score</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">{maxScore > 0 ? displayTotals.maxScore : "—"}</p>
                  <p className="mt-0.5 text-xs text-slate-500">Impact +{scorePercent}%</p>
                </div>
                ) : <div className="min-h-[112px] rounded-xl bg-slate-100 animate-pulse" />}
                {visibleStatsCount >= 3 ? (
                <div className="flex min-h-[112px] flex-col items-center justify-center text-center" style={{ animation: "scorecardItemEnter 320ms ease-out both" }}>
                  <p className="text-xs font-semibold text-slate-600">Qs attended</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">
                    {displayTotals.attendedCount}
                    <span className="text-base font-normal text-slate-400"> / {totalQuestions || questionCount}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">Has submission entries</p>
                </div>
                ) : <div className="min-h-[112px] rounded-xl bg-slate-100 animate-pulse" />}

                {visibleStatsCount >= 4 ? (
                <div className="flex min-h-[112px] flex-col items-center justify-center text-center" style={{ animation: "scorecardItemEnter 320ms ease-out both" }}>
                  <p className="text-xs font-semibold text-slate-600">Qs completed</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">
                    {displayTotals.completedCount}
                    <span className="text-base font-normal text-slate-400"> / {totalQuestions || questionCount}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">Got full score</p>
                </div>
                ) : <div className="min-h-[112px] rounded-xl bg-slate-100 animate-pulse" />}

                {visibleStatsCount >= 5 ? (
                <div className="flex min-h-[112px] flex-col items-center justify-center text-center" style={{ animation: "scorecardItemEnter 320ms ease-out both" }}>
                  <p className="text-xs font-semibold text-slate-600">Languages used</p>
                  <div className="mt-1.5 w-full max-w-[240px]">
                    {languageUsage.length > 0 ? (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {languageUsage.map((item) => (
                          <span
                            key={item.language}
                            className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 shadow-sm border border-slate-100"
                          >
                            <span>{item.language}</span>
                            <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-slate-900 px-1 text-[10px] font-bold text-white">
                              {item.count}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-2xl font-extrabold text-slate-300">-</p>
                    )}
                  </div>
                </div>
                ) : <div className="min-h-[112px] rounded-xl bg-slate-100 animate-pulse" />}
              </div>
            </section>
          ) : null}

          {statsVisible && !breakdownVisible ? (
            <section className="p-3 bg-white shadow-sm rounded-2xl animate-pulse">
              <div className="h-28 rounded-xl bg-slate-100" />
            </section>
          ) : null}

          {breakdownVisible ? (
            <>
              <section className="rounded-2xl bg-white p-2.5 shadow-sm md:p-3" style={{ animation: "fadeIn 420ms ease-out" }}>
                <div className="grid gap-2 lg:grid-cols-12">
                  {allowedMin > 0 && timeCardVisible && (
                    <div className="space-y-2 rounded-xl bg-white p-3 md:p-3.5 lg:col-span-6">
                      <div className="flex items-center justify-between pr-2 md:pr-3">
                        <div>
                          <p className="text-[11px] font-semibold text-slate-500">Time taken</p>
                          <p className="text-[11px] text-slate-500">Allowed {allowedMin} min</p>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-slate-500">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full" /> Used</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-200 rounded-full" /> Remaining</span>
                        </div>
                      </div>
                      <div className="pr-2 md:pr-3">
                        <div
                          className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden flex"
                          style={{ position: "relative" }}
                        >
                          <div
                            className="h-full bg-blue-500"
                            style={{
                              width: `${animatedTakenPct}%`,
                              borderRadius: animatedTakenPct === 100 ? "999px" : "999px 0 0 999px",
                              transition: "width 1300ms cubic-bezier(0.19, 1, 0.22, 1)",
                              position: "relative",
                              overflow: "hidden",
                              boxShadow: "0 0 10px rgba(59,130,246,0.35)",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                inset: 0,
                                background: "linear-gradient(100deg, rgba(255,255,255,0) 10%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 90%)",
                                animation: animatedTakenPct > 0 ? "scorecardProgressShine 1.8s linear infinite" : "none",
                              }}
                            />
                          </div>
                          <div
                            className="h-full bg-blue-200"
                            style={{
                              width: `${animatedRemainingPct}%`,
                              transition: "width 1300ms cubic-bezier(0.19, 1, 0.22, 1)",
                              position: "relative",
                              overflow: "hidden",
                              boxShadow: "inset 0 0 8px rgba(147,197,253,0.35)",
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                inset: 0,
                                background: "linear-gradient(100deg, rgba(255,255,255,0) 10%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 90%)",
                                animation: animatedRemainingPct > 0 ? "scorecardProgressShine 2.2s linear infinite" : "none",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-start gap-6 text-[11px] text-slate-700 pr-2 md:pr-3">
                        <div className="flex flex-col">
                          <span className="text-[11px] font-semibold text-slate-500">Time used</span>
                          <span className="text-sm font-semibold">{takenMin} min ({takenPct}%)</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] font-semibold text-slate-500">Remaining</span>
                          <span className="text-sm font-semibold">{remainingMin} min ({remainingPct}%)</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="h-full lg:col-span-6">
                    <div className="h-full space-y-2 rounded-xl bg-white p-3 md:p-3.5">
                      <p className="text-[11px] font-semibold text-slate-500">Question difficulties</p>
                      {!difficultyCardVisible ? (
                        <div className="grid gap-2.5 sm:grid-cols-3">
                          {[1, 2, 3].map((s) => (
                            <div key={s} className="rounded-xl min-h-[92px] bg-slate-100 animate-pulse" />
                          ))}
                        </div>
                      ) : (
                      <div className="grid gap-2.5 sm:grid-cols-3">
                      {difficultyCoverageRows.map((row, idx) => (
                        <div
                          key={row.key}
                            className={`rounded-xl px-5 py-3.5 min-h-[92px] flex flex-col justify-between transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${row.tone}`}
                            style={{ animation: `scorecardItemEnter 340ms ease-out ${idx * 90}ms both` }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold">{row.label}</span>
                            <span className="text-xs font-extrabold">{row.pct}%</span>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-white/60 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: difficultyBarsVisible ? `${row.pct}%` : "0%",
                                background: "rgba(15,23,42,0.55)",
                                transition: "width 1200ms cubic-bezier(0.19, 1, 0.22, 1)",
                                boxShadow: difficultyBarsVisible ? "0 0 8px rgba(15,23,42,0.25)" : "none",
                                position: "relative",
                                overflow: "hidden",
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  background: "linear-gradient(100deg, rgba(255,255,255,0) 10%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 90%)",
                                  animation: difficultyBarsVisible ? "scorecardProgressShine 2s linear infinite" : "none",
                                }}
                              />
                            </div>
                          </div>
                          <p className="text-xs font-semibold opacity-85">
                            {row.attempted}/{row.total} attempted
                          </p>
                        </div>
                      ))}
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>
      <style>{`
        @keyframes scorecardItemEnter {
          from { opacity: 0; transform: translateY(10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scorecardProgressShine {
          0% { transform: translateX(-140%); }
          100% { transform: translateX(140%); }
        }
      `}</style>
    </StudentLayout>
  );
};

export default AssignmentSubmitSummary;